<script setup lang="ts">
import { BaseSkeleton } from '@/shared/ui/BaseSkeleton';
import { FlowButton } from '@/shared/ui/FlowButton';
import { BASE_CARD_TEST_ID } from './FlowCard.constants';

interface Props {
  title?: string;
  subTitle?: string;
  controlsOff?: boolean;
  buttonText?: string;
  dataTestId?: string;
  loading?: boolean;
  submitLoading?: boolean;
  wide?: boolean;
}

interface Emits {
  (e: 'proceed'): void;
}

withDefaults(defineProps<Props>(), {
  controlsOff: false,
  buttonText: '',
  title: '',
  subTitle: '',
  dataTestId: BASE_CARD_TEST_ID,
  loading: false,
  submitLoading: false,
  wide: false,
});

const emit = defineEmits<Emits>();

function onClick() {
  emit('proceed');
}
</script>

<template>
  <div class="flow-card" :class="{ 'flow-card--wide': wide }">
    <header v-if="title && !loading" class="flow-card-section">
      <h1 class="flow-card__title" :data-testid="dataTestId">
        {{ title }}
      </h1>
      <h2 v-if="subTitle" class="flow-card__subtitle">
        {{ subTitle }}
      </h2>
    </header>

    <slot v-if="loading" name="loading-content">
      <BaseSkeleton fullwidth :height="44" />
      <BaseSkeleton fullwidth :height="200" />
    </slot>

    <div v-else class="flow-card__content flow-card-section" :class="{ 'flow-card__content--wide': wide }">
      <slot />
    </div>

    <div v-if="($slots.controls || !controlsOff) && !loading" class="flow-card__controls flow-card-section">
      <slot name="before-controls" />
      <slot name="controls">
        <FlowButton :loading="submitLoading" size="large" @click="onClick">
          {{ buttonText || $t('qic.continue') }}
        </FlowButton>
      </slot>
      <slot name="after-controls" />
    </div>

    <div v-if="$slots.footer && !loading" class="flow-card__footer flow-card-section">
      <slot name="footer" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use './FlowCard';
</style>
